import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CASH_DENOMINATIONS,
  cashDenomination,
  type CashBreakdown,
  type CashDenominationKey,
  type CashRequestType,
} from '@qargo-ims/types';
import { apiFetch } from '@/lib/api/client';
import { useUserContext } from '@/lib/auth/useUserContext';
import { useViewScope, type ScopeStore } from '@/lib/scope/ViewScope';
import { formatCents, formatShortDate } from '@/lib/format';

interface CashRequest {
  id: string;
  storeId: string;
  amountCents: number;
  requestType: CashRequestType;
  denominations: CashBreakdown | null;
  reason: string;
  status: 'submitted' | 'approved' | 'rejected';
  requestedById: string;
  requestedByTier: 'manager' | 'store_ops';
  decidedById: string | null;
  decidedAt: string | null;
  decisionNotes: string | null;
  createdAt: string;
}

interface ListResponse {
  requests: CashRequest[];
}

const TYPE_LABEL: Record<CashRequestType, string> = {
  additional: 'Additional cash',
  exchange: 'Cash exchange',
};

/** "5 × $20" / "2 × Quarters roll ($10)" lines for a stored breakdown. */
function breakdownLines(breakdown: CashBreakdown): string[] {
  return CASH_DENOMINATIONS.filter((d) => breakdown[d.key]).map((d) =>
    d.kind === 'roll'
      ? `${breakdown[d.key]} × ${d.label} roll (${formatCents(d.valueCents)})`
      : `${breakdown[d.key]} × ${d.label}`,
  );
}

export function CashRequests() {
  const { tier, scopedStores, principal } = useUserContext();
  const { stores, storeLabel, inScope, label: scopeLabel } = useViewScope();
  const [rows, setRows] = useState<CashRequest[]>([]);
  const [filter, setFilter] = useState<'all' | 'submitted' | 'approved' | 'rejected'>('submitted');
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisionTarget, setDecisionTarget] = useState<CashRequest | null>(null);

  const visibleRows = rows.filter((r) => inScope(r.storeId));
  // Stores offered in the create form: the scope selection when one is active,
  // otherwise everything the user is authorized for.
  const formStoreIds = scopedStores.filter(inScope);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    if (filter !== 'all') p.set('status', filter);
    apiFetch<ListResponse>(`/cash-requests?${p.toString()}`)
      .then((r) => setRows(r.requests))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="min-h-screen p-6 max-w-5xl mx-auto">
      <header className="flex items-center justify-between border-b border-border pb-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Cash requests</h1>
          <p className="text-sm text-muted-foreground">
            {tier === 'store_ops' ? 'Shift-lead cap $500 · ' : ''}
            Viewing {scopeLabel}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          New request
        </button>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        >
          <option value="all">all</option>
          <option value="submitted">submitted</option>
          <option value="approved">approved</option>
          <option value="rejected">rejected</option>
        </select>
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}

      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground border-b border-border">
          <tr>
            <th className="py-2">Store</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Reason</th>
            <th>Requested by</th>
            <th>Status</th>
            <th>Created</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r) => {
            const canDecide = r.status === 'submitted' && r.requestedById !== principal?.userId;
            return (
              <tr key={r.id} className="border-b border-border/40">
                <td className="py-2">{storeLabel(r.storeId)}</td>
                <td className="text-xs">{TYPE_LABEL[r.requestType] ?? r.requestType}</td>
                <td className="font-medium">{formatCents(r.amountCents)}</td>
                <td>{r.reason}</td>
                <td>
                  <span className="text-xs text-muted-foreground">
                    {r.requestedByTier} · {r.requestedById.slice(0, 8)}…
                  </span>
                </td>
                <td>
                  <span
                    className={
                      r.status === 'approved'
                        ? 'text-emerald-700'
                        : r.status === 'rejected'
                          ? 'text-destructive'
                          : 'text-amber-700'
                    }
                  >
                    {r.status}
                  </span>
                </td>
                <td className="text-xs text-muted-foreground">{formatShortDate(r.createdAt)}</td>
                <td className="text-right">
                  {canDecide ? (
                    <button
                      type="button"
                      onClick={() => setDecisionTarget(r)}
                      className="text-xs underline underline-offset-4"
                    >
                      Decide
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {!loading && visibleRows.length === 0 ? (
            <tr>
              <td colSpan={8} className="py-6 text-center text-muted-foreground">
                No requests{rows.length > 0 ? ' in the current view scope' : ''}.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {createOpen ? (
        <CreateDialog
          stores={stores}
          storeIds={formStoreIds}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}
      {decisionTarget ? (
        <DecisionDialog
          request={decisionTarget}
          storeLabel={storeLabel}
          onClose={() => setDecisionTarget(null)}
          onDone={() => {
            setDecisionTarget(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDialog({
  stores,
  storeIds,
  onClose,
  onCreated,
}: {
  stores: ScopeStore[];
  /** Store ids offered in the picker — already narrowed to the view scope. */
  storeIds: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { tier } = useUserContext();
  const [requestType, setRequestType] = useState<CashRequestType | null>(null);
  const [storeId, setStoreId] = useState(storeIds[0] ?? '');
  const [counts, setCounts] = useState<Partial<Record<CashDenominationKey, number>>>({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const totalCents = useMemo(
    () =>
      Object.entries(counts).reduce(
        (sum, [key, qty]) =>
          sum + cashDenomination(key as CashDenominationKey).valueCents * (qty ?? 0),
        0,
      ),
    [counts],
  );

  function bump(key: CashDenominationKey, delta: number) {
    setCounts((prev) => {
      const next = Math.max(0, Math.min(999, (prev[key] ?? 0) + delta));
      const copy = { ...prev };
      if (next === 0) delete copy[key];
      else copy[key] = next;
      return copy;
    });
  }

  async function submit() {
    if (!requestType) return;
    if (totalCents <= 0) {
      setErr('add at least one denomination');
      return;
    }
    if (!reason) {
      setErr('reason required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/cash-requests', {
        method: 'POST',
        body: JSON.stringify({
          storeId,
          amountCents: totalCents,
          requestType,
          denominations: counts,
          reason,
        }),
      });
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'submit failed');
    } finally {
      setBusy(false);
    }
  }

  const bills = CASH_DENOMINATIONS.filter((d) => d.kind === 'bill');
  const rolls = CASH_DENOMINATIONS.filter((d) => d.kind === 'roll');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-lg space-y-4 my-auto">
        <h3 className="text-lg font-semibold">New cash request</h3>

        {requestType === null ? (
          <>
            <p className="text-sm text-muted-foreground">What does the store need?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRequestType('additional')}
                className="border border-border rounded-lg p-4 text-left hover:border-primary hover:bg-primary/5"
              >
                <span className="block font-medium">Additional cash</span>
                <span className="block text-xs text-muted-foreground mt-1">
                  The store needs more cash on hand.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setRequestType('exchange')}
                className="border border-border rounded-lg p-4 text-left hover:border-primary hover:bg-primary/5"
              >
                <span className="block font-medium">Cash exchange</span>
                <span className="block text-xs text-muted-foreground mt-1">
                  Swap bills for smaller denominations or coin rolls — same value handed back.
                </span>
              </button>
            </div>
            <div className="flex justify-end pt-2">
              <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{TYPE_LABEL[requestType]}</span>
              <button
                type="button"
                onClick={() => setRequestType(null)}
                className="text-xs underline underline-offset-4 text-muted-foreground"
              >
                Change type
              </button>
            </div>

            <label className="block text-sm">
              Store
              <select
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                className="mt-1 w-full border border-border rounded-md px-2 py-1 text-sm"
              >
                {storeIds.map((id) => {
                  const s = stores.find((x) => x.id === id);
                  return (
                    <option key={id} value={id}>
                      {s ? `${s.code} — ${s.name}` : id}
                    </option>
                  );
                })}
              </select>
            </label>

            <fieldset>
              <legend className="text-sm font-medium mb-1">Bills</legend>
              <div className="grid grid-cols-2 gap-2">
                {bills.map((d) => (
                  <DenominationRow
                    key={d.key}
                    label={d.label}
                    qty={counts[d.key] ?? 0}
                    onBump={(delta) => bump(d.key, delta)}
                  />
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="text-sm font-medium mb-1">Coin rolls</legend>
              <div className="grid grid-cols-2 gap-2">
                {rolls.map((d) => (
                  <DenominationRow
                    key={d.key}
                    label={`${d.label} · ${formatCents(d.valueCents)}`}
                    sublabel={d.detail}
                    qty={counts[d.key] ?? 0}
                    onBump={(delta) => bump(d.key, delta)}
                  />
                ))}
              </div>
            </fieldset>

            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm text-muted-foreground">
                {requestType === 'exchange'
                  ? 'To receive — hand back the same value in other denominations'
                  : 'Total requested'}
              </span>
              <span className="text-lg font-semibold">{formatCents(totalCents)}</span>
            </div>
            {tier === 'store_ops' && totalCents > 500_00 ? (
              <p className="text-xs text-amber-700">Shift leads may request at most $500.</p>
            ) : null}

            <label className="block text-sm">
              Reason
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[60px]"
                placeholder="What is this for?"
              />
            </label>

            {err ? <p className="text-sm text-destructive">{err}</p> : null}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={busy || !storeId || totalCents <= 0}
                className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
              >
                {busy ? 'Submitting…' : `Submit ${totalCents > 0 ? formatCents(totalCents) : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DenominationRow({
  label,
  sublabel,
  qty,
  onBump,
}: {
  label: string;
  sublabel?: string;
  qty: number;
  onBump: (delta: number) => void;
}) {
  return (
    <div className="flex items-center justify-between border border-border rounded-md px-2 py-1.5">
      <div className="min-w-0">
        <span className="block text-sm truncate">{label}</span>
        {sublabel ? <span className="block text-xs text-muted-foreground">{sublabel}</span> : null}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          aria-label={`fewer ${label}`}
          onClick={() => onBump(-1)}
          disabled={qty === 0}
          className="w-7 h-7 rounded-md border border-border text-sm leading-none disabled:opacity-40"
        >
          −
        </button>
        <span className="w-8 text-center text-sm tabular-nums">{qty}</span>
        <button
          type="button"
          aria-label={`more ${label}`}
          onClick={() => onBump(1)}
          className="w-7 h-7 rounded-md border border-border text-sm leading-none"
        >
          +
        </button>
      </div>
    </div>
  );
}

function DecisionDialog({
  request,
  storeLabel,
  onClose,
  onDone,
}: {
  request: CashRequest;
  storeLabel: (id: string) => string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function decide(decision: 'approve' | 'reject') {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/cash-requests/${request.id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision, notes: notes || undefined }),
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'decision failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3">
        <h3 className="text-lg font-semibold">Decide cash request</h3>
        <p className="text-sm">
          <strong>{formatCents(request.amountCents)}</strong> ·{' '}
          {TYPE_LABEL[request.requestType] ?? request.requestType} — {request.reason}
        </p>
        <p className="text-xs text-muted-foreground">{storeLabel(request.storeId)}</p>
        {request.denominations ? (
          <ul className="text-xs text-muted-foreground border border-border rounded-md px-3 py-2 space-y-0.5">
            {breakdownLines(request.denominations).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Requested by {request.requestedByTier} {request.requestedById.slice(0, 8)}…
        </p>
        <label className="block text-sm">
          Notes (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[60px]"
          />
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void decide('reject')}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-destructive text-white text-sm disabled:opacity-60"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => void decide('approve')}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-emerald-700 text-white text-sm disabled:opacity-60"
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
